import React, { useState, useEffect } from 'react';
import {
  Sparkles, Hash, TrendingUp, Calendar, Brain, Lightbulb,
  Copy, RefreshCw, Filter, ChevronDown, ChevronUp, Star,
  Clock, Target, Zap, MessageSquare, Eye, ThumbsUp, BarChart2
} from 'lucide-react';
import { socialMediaAI } from '../services/socialMediaAI';

interface ContentSuggestion {
  id: string;
  content: string;
  platforms: string[];
  tone: string;
  category: string;
  hashtags: string[];
  emojis: string[];
  engagement_score: number;
  best_time_to_post: string;
  character_count: number;
  hooks: string[];
  cta_suggestions: string[];
}

interface HashtagSuggestion {
  tag: string;
  relevance_score: number;
  popularity: string;
  trending: boolean;
  competition_level: number;
  estimated_reach: number;
  related_tags: string[];
}

const SocialMediaAIAssistant: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'content' | 'hashtags' | 'trending' | 'calendar'>('content');
  const [loading, setLoading] = useState(false);
  
  // Content generation state
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState<'professional' | 'casual' | 'enthusiastic' | 'informative' | 'humorous'>('professional');
  const [platforms, setPlatforms] = useState<string[]>(['facebook', 'linkedin']);
  const [contentSuggestions, setContentSuggestions] = useState<ContentSuggestion[]>([]);
  const [selectedContent, setSelectedContent] = useState<ContentSuggestion | null>(null);
  
  // Hashtag generation state
  const [hashtagTopic, setHashtagTopic] = useState('');
  const [hashtagSuggestions, setHashtagSuggestions] = useState<HashtagSuggestion[]>([]);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>([]);
  
  // Trending topics state
  const [trendingTopics, setTrendingTopics] = useState<any[]>([]);
  
  // Content calendar state
  const [contentCalendar, setContentCalendar] = useState<any[]>([]);

  // Load trending topics on mount
  useEffect(() => {
    loadTrendingTopics();
  }, []);

  const generateContent = async () => {
    if (!topic.trim()) return;
    
    setLoading(true);
    try {
      const suggestions = await socialMediaAI.generateContentSuggestions(
        topic,
        platforms,
        tone,
        5
      );
      setContentSuggestions(suggestions);
    } catch (error) {
      console.error('Error generating content:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateHashtags = async () => {
    const searchTopic = hashtagTopic || topic;
    if (!searchTopic.trim()) return;
    
    setLoading(true);
    try {
      const hashtags = await socialMediaAI.generateHashtags(
        searchTopic,
        [],
        30
      );
      setHashtagSuggestions(hashtags);
    } catch (error) {
      console.error('Error generating hashtags:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrendingTopics = async () => {
    setLoading(true);
    try {
      const topics = await socialMediaAI.getTrendingTopics('plexi');
      setTrendingTopics(topics);
    } catch (error) {
      console.error('Error loading trending topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCalendar = async () => {
    setLoading(true);
    try {
      const calendar = await socialMediaAI.generateContentCalendar(30, 2);
      setContentCalendar(calendar);
    } catch (error) {
      console.error('Error generating calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Możesz dodać toast notification
  };

  const toggleHashtag = (tag: string) => {
    setSelectedHashtags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const getPopularityColor = (popularity: string) => {
    switch (popularity) {
      case 'high': return 'text-green-400 bg-green-900/20';
      case 'medium': return 'text-yellow-400 bg-yellow-900/20';
      case 'low': return 'text-orange-400 bg-orange-900/20';
      case 'niche': return 'text-purple-400 bg-purple-900/20';
      default: return 'text-gray-400 bg-gray-900/20';
    }
  };

  const getToneIcon = (tone: string) => {
    switch (tone) {
      case 'professional': return '👔';
      case 'casual': return '😊';
      case 'enthusiastic': return '🎉';
      case 'informative': return '📚';
      case 'humorous': return '😄';
      default: return '💬';
    }
  };

  return (
    <div className="bg-zinc-800 rounded-xl p-6 border border-zinc-700 relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-500" />
          AI Content Assistant
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('content')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'content'
                ? 'bg-purple-600 text-white'
                : 'bg-zinc-700 text-gray-400 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-2" />
            Treść
          </button>
          <button
            onClick={() => setActiveTab('hashtags')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'hashtags'
                ? 'bg-purple-600 text-white'
                : 'bg-zinc-700 text-gray-400 hover:text-white'
            }`}
          >
            <Hash className="w-4 h-4 inline mr-2" />
            Hashtagi
          </button>
          <button
            onClick={() => setActiveTab('trending')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'trending'
                ? 'bg-purple-600 text-white'
                : 'bg-zinc-700 text-gray-400 hover:text-white'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Trendy
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'calendar'
                ? 'bg-purple-600 text-white'
                : 'bg-zinc-700 text-gray-400 hover:text-white'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Kalendarz
          </button>
        </div>
      </div>

      {/* Content Generation Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          {/* Input Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                O czym chcesz napisać?
              </label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="np. Nowy ekspozytor z plexi, promocja świąteczna, poradnik..."
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Ton wypowiedzi
              </label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as any)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="professional">👔 Profesjonalny</option>
                <option value="casual">😊 Swobodny</option>
                <option value="enthusiastic">🎉 Entuzjastyczny</option>
                <option value="informative">📚 Informacyjny</option>
                <option value="humorous">😄 Humorystyczny</option>
              </select>
            </div>
          </div>

          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Platformy docelowe
            </label>
            <div className="flex flex-wrap gap-2">
              {['facebook', 'linkedin', 'instagram', 'twitter'].map(platform => (
                <button
                  key={platform}
                  onClick={() => setPlatforms(prev =>
                    prev.includes(platform)
                      ? prev.filter(p => p !== platform)
                      : [...prev, platform]
                  )}
                  className={`px-4 py-2 rounded-lg capitalize transition-all ${
                    platforms.includes(platform)
                      ? 'bg-purple-600 text-white'
                      : 'bg-zinc-700 text-gray-400 hover:text-white'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateContent}
            disabled={loading || !topic.trim()}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <RefreshCw className="w-5 h-5 inline mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5 inline mr-2" />
            )}
            Generuj sugestie treści
          </button>

          {/* Content Suggestions */}
          {contentSuggestions.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Sugestie treści ({contentSuggestions.length})
              </h3>
              
              {contentSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion.id}
                  className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 hover:border-purple-500 transition-all cursor-pointer"
                  onClick={() => setSelectedContent(suggestion)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{getToneIcon(suggestion.tone)}</span>
                      <span className="text-sm text-gray-400 capitalize">{suggestion.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 text-sm">
                        <Target className="w-4 h-4 text-green-400" />
                        <span className="text-green-400">{suggestion.engagement_score}%</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(suggestion.content);
                        }}
                        className="p-2 hover:bg-zinc-800 rounded-lg transition-all"
                      >
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <p className="text-white mb-3 whitespace-pre-wrap">{suggestion.content}</p>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {suggestion.best_time_to_post}
                      </span>
                      <span>{suggestion.character_count} znaków</span>
                    </div>
                    <div className="flex gap-1">
                      {suggestion.platforms.map(p => (
                        <span key={p} className="px-2 py-1 bg-zinc-800 rounded text-xs capitalize">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>

                  {selectedContent?.id === suggestion.id && (
                    <div className="mt-4 pt-4 border-t border-zinc-700 space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-2">Hashtagi:</p>
                        <div className="flex flex-wrap gap-1">
                          {suggestion.hashtags.map(tag => (
                            <span key={tag} className="text-blue-400 text-sm">#{tag}</span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-2">Hooki:</p>
                        <ul className="space-y-1">
                          {suggestion.hooks.map((hook, i) => (
                            <li key={i} className="text-sm text-gray-300">• {hook}</li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-400 mb-2">CTA:</p>
                        <div className="flex flex-wrap gap-2">
                          {suggestion.cta_suggestions.map((cta, i) => (
                            <span key={i} className="px-2 py-1 bg-zinc-800 rounded text-sm text-gray-300">
                              {cta}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hashtag Generation Tab */}
      {activeTab === 'hashtags' && (
        <div className="space-y-6">
          {/* Input Section */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Temat lub branża
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={hashtagTopic}
                onChange={(e) => setHashtagTopic(e.target.value)}
                placeholder="np. ekspozytory, produkcja, plexi, b2b..."
                className="flex-1 px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
              <button
                onClick={generateHashtags}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Hash className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Selected Hashtags */}
          {selectedHashtags.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-400">
                  Wybrane hashtagi ({selectedHashtags.length})
                </h4>
                <button
                  onClick={() => copyToClipboard(selectedHashtags.map(t => `#${t}`).join(' '))}
                  className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-all"
                >
                  <Copy className="w-3 h-3 inline mr-1" />
                  Kopiuj
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedHashtags.map(tag => (
                  <span
                    key={tag}
                    onClick={() => toggleHashtag(tag)}
                    className="px-3 py-1 bg-purple-900/30 border border-purple-600 rounded-full text-purple-400 text-sm cursor-pointer hover:bg-purple-900/50 transition-all"
                  >
                    #{tag} ×
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hashtag Suggestions */}
          {hashtagSuggestions.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">
                Sugerowane hashtagi
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {hashtagSuggestions.map((hashtag) => (
                  <div
                    key={hashtag.tag}
                    onClick={() => toggleHashtag(hashtag.tag)}
                    className={`bg-zinc-900 border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedHashtags.includes(hashtag.tag)
                        ? 'border-purple-500 bg-purple-900/20'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-white font-medium">#{hashtag.tag}</span>
                      {hashtag.trending && (
                        <span className="px-2 py-1 bg-red-900/30 text-red-400 text-xs rounded">
                          Trending 🔥
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Popularność:</span>
                        <span className={`px-2 py-1 rounded ${getPopularityColor(hashtag.popularity)}`}>
                          {hashtag.popularity}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Zasięg:</span>
                        <span className="text-gray-400">
                          {hashtag.estimated_reach.toLocaleString()}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Konkurencja:</span>
                        <div className="flex items-center gap-1">
                          <div className="w-20 h-2 bg-zinc-800 rounded-full">
                            <div
                              className={`h-2 rounded-full ${
                                hashtag.competition_level > 70 ? 'bg-red-500' :
                                hashtag.competition_level > 40 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${hashtag.competition_level}%` }}
                            />
                          </div>
                          <span className="text-gray-400">{Math.round(hashtag.competition_level)}%</span>
                        </div>
                      </div>
                    </div>

                    {hashtag.related_tags.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-zinc-800">
                        <p className="text-xs text-gray-500 mb-1">Powiązane:</p>
                        <div className="flex flex-wrap gap-1">
                          {hashtag.related_tags.slice(0, 3).map(tag => (
                            <span key={tag} className="text-xs text-blue-400">#{tag}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trending Topics Tab */}
      {activeTab === 'trending' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              Trendy w Twojej branży
            </h3>
            <button
              onClick={loadTrendingTopics}
              className="px-4 py-2 bg-zinc-700 text-gray-300 rounded-lg hover:bg-zinc-600 transition-all"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Odśwież
            </button>
          </div>

          <div className="space-y-4">
            {trendingTopics.map((topic, index) => (
              <div key={index} className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-white font-medium mb-1">{topic.topic}</h4>
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {topic.volume.toLocaleString()} wyszukiwań
                      </span>
                      <span className="flex items-center gap-1 text-green-400">
                        <TrendingUp className="w-4 h-4" />
                        +{topic.growth_rate}%
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setTopic(topic.topic)}
                    className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-all"
                  >
                    Użyj
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Pomysły na treść:</p>
                    <ul className="space-y-1">
                      {topic.content_ideas.map((idea: string, i: number) => (
                        <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                          <span className="text-purple-400 mt-0.5">•</span>
                          <span>{idea}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Hashtagi:</p>
                    <div className="flex flex-wrap gap-1">
                      {topic.related_hashtags.map((tag: string) => (
                        <span key={tag} className="text-xs text-blue-400">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              Kalendarz treści na 30 dni
            </h3>
            <button
              onClick={generateCalendar}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 transition-all"
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Generuj kalendarz
            </button>
          </div>

          {contentCalendar.length > 0 && (
            <div className="space-y-3">
              {/* Calendar Header */}
              <div className="grid grid-cols-7 gap-2 text-center">
                {['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'].map(day => (
                  <div key={day} className="text-xs font-medium text-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {contentCalendar.slice(0, 30).map((item, index) => {
                  const date = new Date(item.date);
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  
                  return (
                    <div
                      key={index}
                      className={`bg-zinc-900 border rounded-lg p-2 min-h-[100px] cursor-pointer hover:border-purple-500 transition-all ${
                        isWeekend ? 'border-zinc-800' : 'border-zinc-700'
                      }`}
                    >
                      <div className="text-xs text-gray-500 mb-1">
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        <div className="text-xs bg-purple-900/30 text-purple-400 rounded px-1 py-0.5">
                          {item.time}
                        </div>
                        <div className="text-xs text-gray-300 line-clamp-2">
                          {item.content.content.substring(0, 50)}...
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Detailed View */}
              <div className="mt-6 space-y-3">
                <h4 className="text-md font-medium text-white">Szczegółowy widok</h4>
                {contentCalendar.slice(0, 7).map((item, index) => (
                  <div key={index} className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-white">
                          {new Date(item.date).toLocaleDateString('pl-PL', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long' 
                          })}
                        </span>
                        <span className="px-2 py-1 bg-blue-900/30 text-blue-400 text-xs rounded">
                          {item.time}
                        </span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(item.content.content)}
                        className="p-2 hover:bg-zinc-800 rounded-lg transition-all"
                      >
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    <p className="text-sm text-gray-300 mb-2">{item.content.content}</p>
                    <p className="text-xs text-gray-500 italic">
                      💡 {item.reason}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {contentCalendar.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Kliknij "Generuj kalendarz" aby utworzyć plan publikacji na 30 dni</p>
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center rounded-xl">
          <div className="text-center">
            <Brain className="w-12 h-12 text-purple-500 animate-pulse mx-auto mb-3" />
            <p className="text-white font-medium">AI analizuje...</p>
            <p className="text-gray-400 text-sm mt-1">To może potrwać kilka sekund</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaAIAssistant;
